import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Minimize2, Maximize2 } from 'lucide-react';
import ChatMessage from './ChatMessage';
import FAQButtons from './FAQButtons';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showFAQs, setShowFAQs] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Initial greeting when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 1,
        sender: 'bot',
        text: "Hello! I'm Dr. Veena, your AI Medical Guide at MedPlexity. 👋\n\nI can help you:\n• Navigate MedPlexity features\n• Understand your health journey options\n• Answer questions about our platform\n\nHow can I assist you today?",
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      text: text.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setShowFAQs(false);
    setIsTyping(true);

    try {
      // Get conversation history for context
      const conversationHistory = messages.slice(-10).map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text
      }));

      const response = await fetch(`${backendUrl}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          conversation_history: conversationHistory
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          sender: 'bot',
          text: data.response,
          navigation: data.navigation,
          timestamp: new Date()
        }]);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        sender: 'bot',
        text: "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or feel free to explore MedPlexity's features directly from the navigation menu.",
        timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  }, [backendUrl, messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleFAQClick = (question) => {
    sendMessage(question);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0277bd 0%, #01579b 100%)',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(2, 119, 189, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 9999
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 24px rgba(2, 119, 189, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(2, 119, 189, 0.4)';
          }}
          aria-label="Open chat with Dr. Veena"
        >
          <MessageCircle size={28} color="white" />
          {/* Notification dot */}
          <span style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: '#4caf50',
            border: '2px solid white'
          }} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            width: isMinimized ? '320px' : '380px',
            height: isMinimized ? 'auto' : '600px',
            maxHeight: '80vh',
            borderRadius: '16px',
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 9999,
            background: 'white'
          }}
        >
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #0277bd 0%, #01579b 100%)',
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            {/* Avatar */}
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              👩‍⚕️
            </div>
            
            <div style={{ flex: 1 }}>
              <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                Dr. Veena
              </div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#4caf50'
                }} />
                AI Medical Guide
              </div>
            </div>
            
            {/* Controls */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={toggleMinimize}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {isMinimized ? <Maximize2 size={16} color="white" /> : <Minimize2 size={16} color="white" />}
              </button>
              <button
                onClick={toggleChat}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={16} color="white" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          {!isMinimized && (
            <>
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '1rem',
                background: '#f5f7fa',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}
                
                {/* Typing Indicator */}
                {isTyping && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    background: 'white',
                    borderRadius: '16px',
                    borderBottomLeftRadius: '4px',
                    alignSelf: 'flex-start',
                    maxWidth: '200px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                  }}>
                    <span style={{ color: '#78909c', fontSize: '0.9rem' }}>Dr. Veena is typing</span>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {[0, 1, 2].map(i => (
                        <span
                          key={i}
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#0277bd',
                            animation: `bounce 1.4s infinite ${i * 0.2}s`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* FAQ Buttons */}
                {showFAQs && messages.length <= 1 && (
                  <FAQButtons onFAQClick={handleFAQClick} />
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <form
                onSubmit={handleSubmit}
                style={{
                  padding: '1rem',
                  background: 'white',
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  gap: '0.75rem'
                }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type your message..."
                  disabled={isTyping}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '24px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0277bd'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isTyping}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: inputValue.trim() && !isTyping 
                      ? 'linear-gradient(135deg, #0277bd 0%, #01579b 100%)' 
                      : '#e0e0e0',
                    border: 'none',
                    cursor: inputValue.trim() && !isTyping ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                >
                  {isTyping ? (
                    <Loader2 size={20} color="white" className="spinning" />
                  ) : (
                    <Send size={20} color="white" />
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
